import { useCallback, useMemo, useState } from 'react'
import {
  GroupPadded,
  GroupPaddedGrow,
  LeftContent,
  RightContent,
  ScreenContainer
} from '../common/TaskViewContainerStyle'
import ContextGroup from '../common/task-context/ContextGroup'
import AssessmentForm from './AssessmentForm'

import countries from 'i18n-iso-countries'
import { ArrowDownTrayIcon } from '../../icons/ArrayDownTrayIcon'
import { ClipboardIcon } from '../../icons/Clipboard'
import { formatMoney } from '../../utils/formatMoney'
import { isSwedenPortal } from '../../utils/portalCountry'
countries.registerLocale(require('i18n-iso-countries/langs/en.json'))

const base64ToBlob = (b64: string, mime = '') =>
  new Blob([Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))], { type: mime })

type FileLike = { fileName: string; mimeType?: string; content: string }

const isFileLike = (v: unknown): v is FileLike =>
  !!v && typeof v === 'object' && 'fileName' in v && 'content' in v

const isFileLikeArray = (v: unknown): v is FileLike[] =>
  Array.isArray(v) && v.every(isFileLike)

const isAllUppercase = (v: string) => v === v.toUpperCase()

const downloadFile = ({ fileName, mimeType, content }: FileLike) => {
  const url = URL.createObjectURL(base64ToBlob(content, mimeType))
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

const formatValue = (v: unknown): string =>
  typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v)

const LabelValue = ({
  t,
  label,
  value
}: {
  t: (s: string) => string
  label: string
  value: unknown
}) => {
  const [copied, setCopied] = useState(false)

  const isCountryCode = (val: string) =>
    typeof val === 'string' && val.length === 2 && countries.isValid(val)

  const getTranslationKey = (key: string) => {
    const seSpecificKeys = [
      'hasOperations',
      'hasRegistrations',
      'transactionCountries'
    ]
    if (isSwedenPortal && seSpecificKeys.includes(key)) {
      return `${key}-se`
    }
    return key
  }

  const displayValue = useMemo(() => {
    if (isFileLikeArray(value)) return value.map((f) => f.fileName).join(', ')
    if (isFileLike(value)) return value.fileName
    if (Array.isArray(value)) {
      const isArrayOfCountries = value.every((v) => isCountryCode(v))
      return isArrayOfCountries
        ? value.map((c) => countries.getName(c, 'en')).join(', ')
        : value.map(formatValue).join(', ')
    }
    if (
      typeof value === 'string' &&
      isAllUppercase(value) &&
      isCountryCode(value)
    ) {
      return countries.getName(value, 'en')
    }
    if (value && typeof value === 'object')
      return Object.entries(value)
        .map(([k, v]) => `${k}: ${formatValue(v)}`)
        .join(', ')

    return formatValue(value)
  }, [value])

  const handleClick = useCallback(() => {
    if (isFileLike(value)) {
      downloadFile(value)
    } else if (isFileLikeArray(value)) {
      value.forEach(downloadFile)
    } else if (displayValue) {
      navigator.clipboard.writeText(displayValue).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      })
    }
  }, [value, displayValue])

  const showsFileIcon = isFileLike(value) || isFileLikeArray(value)

  return (
    <div
      onClick={handleClick}
      className="mb-3 cursor-pointer group"
      role="button"
    >
      <div className="text-sm text-gray-500 font-medium">
        {t(getTranslationKey(label))}
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <span className="flex items-center text-base text-black break-words whitespace-pre-wrap group-hover:text-blue-700">
          {displayValue}
          {showsFileIcon ? (
            <ArrowDownTrayIcon className="ml-2 h-4 w-4 text-gray-400 group-hover:text-blue-600 shrink-0" />
          ) : copied ? (
            <span className="ml-2">✓</span>
          ) : (
            <ClipboardIcon
              size={16}
              className="ml-2 text-gray-400 group-hover:text-blue-600 shrink-0"
            />
          )}
        </span>
      </div>
    </div>
  )
}

const isValidArray = (arr: any) =>
  Array.isArray(arr) ? arr.filter(Boolean).length > 0 : true

const CopyAssessmentScreen = ({
  task,
  flow,
  t,
  onComplete,
  onSave,
  close,
  updateCase,
  user,
  schema
}) => {
  const { customer } = task.context
  const { assetInfo } = flow.data
  const declaration = customer?.customerDeclaration || {}

  function onFormSubmit(formData: FormData, callback = () => {}) {
    const assessmentData = {
      ...formData,
      name: user.profile.name,
      username: user.profile.username,
      sub: user.profile.sub
    }

    onComplete(
      task.taskId,
      assessmentData,
      () => {
        updateCase()
        close()
      },
      () => {
        callback()
      }
    )
  }

  function onFormSave(formData: FormData, callback = () => {}) {
    onSave(
      task.taskId,
      formData,
      () => {
        updateCase()
        close()
      },
      () => {
        callback()
      }
    )
  }

  const riskClassOptions = schema.properties.riskClass.enum.map((a, i) => ({
    value: a,
    label: t(schema.properties.riskClass.enumNames[i])
  }))

  const sections = useMemo(() => {
    return [
      {
        title: t('company-information'),
        fields: {
          ...declaration.companyInformation,
          dateOfIncorporation: undefined,
          registeredAddress: undefined,
          registrationNumber: undefined,
          registeredCountry: undefined,
          hasAlternateAddresses: undefined,
          alternateAddressText: undefined,
          alternateAddressCountry: undefined
        }
      },
      {
        title: t('business-locations'),
        fields: {
          hasOperations: declaration.businessLocations?.hasOperations,
          operationCountries: declaration.businessLocations?.operationCountries,
          hasRegistrations: declaration.businessLocations?.hasRegistrations,
          registrationCountries:
            declaration.businessLocations?.registrationCountries,
          taxResidenceCountries:
            declaration.businessLocations?.taxResidenceCountries,
          tinNumbers: declaration.businessLocations?.tinNumbers
        }
      },
      {
        title: 'Address/contact info',
        fields: {
          registeredAddress: declaration.companyInformation?.registeredAddress,
          registrationNumber:
            declaration.companyInformation?.registrationNumber,
          registeredCountry: declaration.companyInformation?.registeredCountry,
          hasAlternateAddresses:
            declaration.companyInformation?.hasAlternateAddresses,
          alternateAddressText:
            declaration.companyInformation?.alternateAddressText,
          alternateAddressCountry:
            declaration.companyInformation?.alternateAddressCountry
        }
      },
      {
        title: t('business-operations'),
        fields: {
          businessDescription:
            declaration.businessOperations?.businessDescription,
          ...declaration.businessOperations,
          ownershipStructure: undefined
        }
      },
      {
        title: t('business-transactions'),
        fields: {
          ...declaration.businessTransactions,
          transactionSources: undefined
        }
      },
      {
        title: t('source-of-income'),
        fields: {
          transactionSources: mapTransactionSourceLabels(
            declaration.businessTransactions?.transactionSources
          )
        }
      },
      ...(declaration.businessRepresentatives?.representatives || []).map(
        (rep, i) => ({
          title: `${t('associated-party')} #${i + 1}`,
          fields: {
            fullName: rep.fullName,
            fullPersonalNumber: rep.fullPersonalNumber,
            ...rep,
            role: undefined,
            typeOfAssociatedParty: rep.role
          }
        })
      ),
      {
        title: t('ownership-structure'),
        fields: {
          ownershipStructure:
            declaration.businessOperations?.ownershipStructure,
          ownershipStructureFiles:
            declaration.businessOperations?.ownershipStructureFiles
        }
      }
    ]
  }, [declaration, t])

  return (
    <ScreenContainer>
      <LeftContent style={{ justifyContent: 'flex-start', padding: '1rem' }}>
        <div className="w-full">
          <div className="grid grid-cols-1 gap-3">
            {sections.map((section) => (
              <div
                key={section.title}
                className="bg-white border p-4 rounded shadow-sm"
              >
                <h2 className="text-lg font-semibold mb-2">{section.title}</h2>
                {Object.entries(section.fields || {})
                  .filter(
                    ([key, val]) =>
                      val &&
                      val !== '' &&
                      isValidArray(val) &&
                      !['id', 'personalNumber', 'hasOwnershipPercent'].includes(
                        key
                      )
                  )
                  .map(([key, val]) => (
                    <LabelValue
                      t={t}
                      key={key}
                      label={key === 'role' ? 'typeOfAssociatedParty' : key}
                      value={val}
                    />
                  ))}
              </div>
            ))}
          </div>
        </div>
      </LeftContent>

      <RightContent>
        <GroupPaddedGrow>
          <ContextGroup
            title={customer.name}
            context={{
              id: customer.id,
              phone: customer.phone,
              email: customer.email,
              submittedBy: customer.submittedBy,
              ...(assetInfo?.model && {
                object: `${assetInfo?.brandName} ${assetInfo?.model} (${assetInfo?.year})`,
                purchaseAmount: formatMoney(assetInfo?.purchaseAmount, true),
                upfrontAmount: formatMoney(assetInfo?.upfrontAmount, true),
                financedAmount: formatMoney(assetInfo?.financedAmount, true),
                monthlyAmount: formatMoney(assetInfo?.periodicPayment, true)
              })
            }}
          />
        </GroupPaddedGrow>
        <GroupPadded>
          <AssessmentForm
            riskClassOptions={riskClassOptions}
            taskdata={task.data}
            onSubmit={(formData: FormData, cb: (() => void) | undefined) =>
              onFormSubmit(formData, cb)
            }
            onSave={(formData: FormData, cb: (() => void) | undefined) =>
              onFormSave(formData, cb)
            }
          />
        </GroupPadded>
      </RightContent>
    </ScreenContainer>
  )
}

function mapTransactionSourceLabels(sources: string[]) {
  return sources.map((source) => {
    switch (source) {
      case 'salesB2B':
        return 'Products sold to business customers'
      case 'salesB2C':
        return 'Products sold to consumers'
      case 'servicesB2B':
        return 'Service sold to business customers'
      case 'servicesB2C':
        return 'Service sold to consumer customers'
      case 'investments':
        return 'Investments'
      case 'grants':
        return 'Grants'
      case 'membershipFees':
        return 'Membership fees'
      default:
        return source
    }
  })
}

export default CopyAssessmentScreen
